/**
 * @fileoverview Plan limits utilities.
 * @summary Centralized logic for determining plan limits for organizers and events.
 *
 * @package KaraoNow
 * @module packages/shared/src/utils/planLimits
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */
import { SubscriptionType, SUBSCRIPTION_PLANS } from '../constants/plans.js';
/**
 * Get the effective limits for an organizer's events.
 *
 * Logic:
 * 1. If organizer is ULTIMATE → No limits at all
 * 2. If organizer is PRO/PREMIUM → Monthly quota (rolling period based on billing cycle)
 * 3. If organizer is FREE → Max 4 total events (all statuses, excluding ONE_SHOT and 'subscription')
 *
 * @param user - The authenticated user (organizer)
 * @returns Object with limit information
 */
export function getOrganizerLimits(user) {
    if (!user) {
        return {
            maxEvents: SUBSCRIPTION_PLANS[SubscriptionType.FREE].maxEvents,
            hasEventLimit: true,
            userPlan: SubscriptionType.FREE,
            isPremium: false,
            isUltimate: false,
            isMonthlyQuota: false,
        };
    }
    const userPlan = user.subscription?.type || SubscriptionType.FREE;
    const isUltimate = userPlan === SubscriptionType.ULTIMATE;
    const isPremium = userPlan === SubscriptionType.PREMIUM;
    const isPro = userPlan === SubscriptionType.PRO;
    const isMonthlyQuota = isPro || isPremium;
    return {
        maxEvents: isUltimate ? Infinity : SUBSCRIPTION_PLANS[userPlan]?.maxEvents || SUBSCRIPTION_PLANS[SubscriptionType.FREE].maxEvents,
        hasEventLimit: !isUltimate,
        userPlan,
        isPremium,
        isUltimate,
        isMonthlyQuota,
    };
}
/**
 * Get the effective song limit for a specific event.
 *
 * Logic:
 * 1. If organizer is PRO/PREMIUM/ULTIMATE → No limit
 * 2. If organizer is FREE:
 *    - Event is ONE_SHOT or 'subscription' → No limit
 *    - Event is FREE → Apply FREE plan limit
 *
 * @param user - The authenticated user (organizer)
 * @param event - The event to check
 * @returns Object with song limit information
 */
export function getEventSongLimit(user, event) {
    // Check event plan type first (source of truth)
    const eventPlan = event?.planType || SubscriptionType.FREE;
    // ONE_SHOT and subscription events have no song limit
    if (eventPlan === SubscriptionType.ONE_SHOT || eventPlan === 'subscription') {
        return {
            maxSongs: Infinity,
            hasLimit: false,
        };
    }
    // Paid plan users (PRO, PREMIUM, ULTIMATE) have no limits (fallback for old events without planType)
    const { isPremium, isUltimate } = getOrganizerLimits(user);
    const isPro = user?.subscription?.type === SubscriptionType.PRO;
    if (isPremium || isPro || isUltimate) {
        return {
            maxSongs: Infinity,
            hasLimit: false,
        };
    }
    // FREE events have the FREE plan limit
    return {
        maxSongs: SUBSCRIPTION_PLANS[SubscriptionType.FREE].maxSongsInQueue,
        hasLimit: true,
    };
}
/**
 * Check if an organizer can create a new event.
 *
 * Logic:
 * 1. If organizer is ULTIMATE → Always yes
 * 2. If organizer is PRO/PREMIUM → Check monthly quota (rolling period based on billing cycle)
 * 3. If organizer is FREE → Check if under max events limit (4, excluding ONE_SHOT)
 *
 * Note:
 * - For FREE: currentEventsCount should be the count of FREE events only (ONE_SHOT and 'subscription' excluded)
 * - For PRO/PREMIUM: currentEventsCountInPeriod should be the count of 'subscription' events created in the current billing period (ONE_SHOT and FREE excluded)
 *
 * @param user - The authenticated user (organizer)
 * @param currentEventsCount - Current number of FREE events (for FREE plan) or 'subscription' events in current billing period (for PRO/PREMIUM)
 * @returns Object with result and details
 */
export function canCreateEvent(user, currentEventsCount) {
    const { isUltimate, maxEvents } = getOrganizerLimits(user);
    if (isUltimate) {
        return {
            canCreate: true,
            reason: null,
        };
    }
    if (currentEventsCount >= maxEvents) {
        return {
            canCreate: false,
            reason: 'MAX_EVENTS_REACHED',
            currentCount: currentEventsCount,
            limit: maxEvents,
        };
    }
    return {
        canCreate: true,
        reason: null,
    };
}
/**
 * Get the current billing period for monthly quota calculation.
 *
 * Uses subscriptionPeriodEnd (from Stripe current_period_end) if available.
 * Falls back to calculating +30 days from subscriptionStartDate if needed.
 *
 * @param user - The authenticated user (organizer)
 * @returns Object with period start and end dates, or null if not applicable
 */
export function getBillingPeriod(user) {
    if (!user?.subscription?.subscriptionStartDate) {
        return null;
    }
    const start = new Date(user.subscription.subscriptionStartDate);
    // Validate start date
    if (isNaN(start.getTime())) {
        return null;
    }
    // Try to use subscriptionPeriodEnd if available (preferred, from Stripe)
    let end;
    if (user.subscription.subscriptionPeriodEnd) {
        end = new Date(user.subscription.subscriptionPeriodEnd);
        // Validate end date
        if (isNaN(end.getTime()) || start >= end) {
            // Invalid end date, fallback to calculation
            end = new Date(start);
            end.setMonth(end.getMonth() + 1);
        }
    }
    else {
        // Fallback: calculate +1 month from start
        end = new Date(start);
        end.setMonth(end.getMonth() + 1);
    }
    return { start, end };
}
/**
 * Check if songs can be added to an event's queue.
 *
 * Logic:
 * 1. If organizer is PREMIUM → Always yes
 * 2. If organizer is FREE:
 *    - Event is ONE_SHOT or 'subscription' → Always yes
 *    - Event is FREE → Check if under max songs limit
 *
 * @param user - The authenticated user (organizer)
 * @param event - The event to check
 * @param currentSongsCount - Current number of songs in queue
 * @returns Object with result and details
 */
export function canAddSongToQueue(user, event, currentSongsCount) {
    const { maxSongs, hasLimit } = getEventSongLimit(user, event);
    if (!hasLimit) {
        return {
            canAdd: true,
            reason: null,
        };
    }
    if (currentSongsCount >= maxSongs) {
        return {
            canAdd: false,
            reason: 'MAX_SONGS_REACHED',
            currentCount: currentSongsCount,
            limit: maxSongs,
        };
    }
    return {
        canAdd: true,
        reason: null,
    };
}
/**
 * Check if an organizer should see the upgrade to ONE_SHOT button.
 *
 * Logic:
 * - Only show if:
 *   1. User is not ULTIMATE (PRO and PREMIUM can use ONE_SHOT if monthly quota is reached)
 *   2. Event is FREE
 *   3. Event is not completed or cancelled
 *   4. For PRO/PREMIUM: Only show if monthly quota is reached (currentEventsCountInPeriod >= maxEvents)
 *
 * @param user - The authenticated user (organizer)
 * @param event - The event to check
 * @param currentEventsCountInPeriod - Optional: Number of events created in current billing period (for PRO/PREMIUM quota check)
 * @returns true if button should be shown
 */
export function shouldShowOneShotUpgrade(user, event, currentEventsCountInPeriod) {
    if (!event)
        return false;
    const { isUltimate, isMonthlyQuota, maxEvents } = getOrganizerLimits(user);
    // Only ULTIMATE users don't need ONE_SHOT (they have unlimited events)
    if (isUltimate)
        return false;
    // Only for FREE events
    const eventPlan = event.planType || SubscriptionType.FREE;
    if (eventPlan !== SubscriptionType.FREE)
        return false;
    // Not for completed or cancelled events
    if (event.status === 'completed' || event.status === 'cancelled')
        return false;
    // For PRO/PREMIUM plans: Only show if monthly quota is reached
    if (isMonthlyQuota) {
        // If currentEventsCountInPeriod is provided, check if quota is reached
        if (currentEventsCountInPeriod !== undefined) {
            return currentEventsCountInPeriod >= maxEvents;
        }
        // If not provided, default to false (don't show) to avoid showing button when quota is not reached
        // This is a safety measure: if we can't verify the quota, don't show the button
        return false;
    }
    // For FREE plan: Always show (they have a hard limit of 4 events total)
    return true;
}
//# sourceMappingURL=planLimits.js.map